import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import ContactCTA from '../components/ContactCTA';
import { fetchPost, fetchPosts } from '../api';
import { organizationSchema, breadcrumbSchema, combineSchemas } from '../lib/schema';
import { SITE_URL } from '../components/SEO';
import { Calendar, User, ArrowLeft, Loader2 } from 'lucide-react';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchPost(slug)
      .then(async (data) => {
        setPost(data);
        const all = await fetchPosts({ category: data.category });
        setRelated(all.filter((p) => p.slug !== data.slug).slice(0, 3));
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Layout><div className="py-40 flex justify-center"><Loader2 className="animate-spin text-[#2C5F66]" size={40} /></div></Layout>;
  if (notFound || !post) return <Layout><div className="py-40 text-center"><h1 className="text-3xl font-bold text-[#2C5F66]">Post not found</h1><Link to="/blog" className="inline-block mt-6 text-[#D9A04A] font-semibold">← Back to blog</Link></div></Layout>;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    datePublished: post.published_at,
    dateModified: post.published_at,
    author: { '@type': 'Organization', name: post.author },
    publisher: { '@type': 'Organization', name: 'Website Symphony', logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` } },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` },
  };

  return (
    <Layout>
      <SEO
        title={`${post.title} | Website Symphony`}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogImage={post.cover_image}
        keywords={post.tags.join(', ')}
        schema={combineSchemas(articleSchema, organizationSchema, breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }, { name: post.title, path: `/blog/${post.slug}` }]))}
      />

      <article className="bg-white">
        <div className="relative h-[380px] md:h-[460px] overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-12">
            <div className="max-w-4xl mx-auto">
              <span className="inline-block bg-[#D9A04A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">{post.category}</span>
              <h1 className="text-white text-3xl md:text-5xl font-extrabold mt-4 leading-tight drop-shadow">{post.title}</h1>
              <div className="flex items-center gap-5 mt-5 text-white/80 text-sm">
                <span className="inline-flex items-center gap-2"><User size={14} /> {post.author}</span>
                <span className="inline-flex items-center gap-2"><Calendar size={14} /> {new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-16">
          <Link to="/blog" className="inline-flex items-center gap-1 text-[#D9A04A] hover:text-[#B17A24] font-semibold text-sm mb-8"><ArrowLeft size={14} /> Back to all articles</Link>
          <div className="prose max-w-none blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />
          <div className="mt-10 pt-8 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-[#F7F7F5] text-[#2C5F66] text-xs font-medium">#{t}</span>
              ))}
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-16 bg-[#F7F7F5]">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-[#2C5F66] text-2xl md:text-3xl font-extrabold mb-8">Related articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all">
                  <img src={p.cover_image} alt={p.title} loading="lazy" className="h-44 w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="p-5">
                    <span className="text-[#D9A04A] text-xs font-semibold uppercase tracking-wider">{p.category}</span>
                    <h3 className="mt-2 text-[#2C5F66] font-bold group-hover:text-[#D9A04A]">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <ContactCTA defaultService={`Blog: ${post.category}`} heading="Enjoyed the read? Let's build something." subheading="Free, no-obligation consultation. Get a custom quote in 24 hours." />
    </Layout>
  );
};

export default BlogPostPage;
